import { apiClient } from './client';

/**
 * Client for `/api/documents/*` (ADR-035 — the general document repository).
 * Stays in lockstep with `server/src/routes/documents.routes.ts` +
 * `server/src/modules/documents/documents.service.ts`.
 *
 * A document is MULTI-ORG: its bytes reuse the F1 file storage and access is
 * granted per-org to roles / seats / departments / members. `manage_documents`
 * gates management; everyone else VIEWs whatever their role/seat grants.
 *
 * The AI access-suggestion endpoint degrades to 503 `ai_disabled` when AI is
 * not configured for the org; that case is surfaced as a typed
 * {@link AiDisabledError} so the wizard can show a friendly notice rather than
 * a scary error.
 */

// ── Shared DTO types (mirror the server contract exactly) ─────────────────

export type DocumentTargetType = 'all' | 'role' | 'seat' | 'department' | 'membership';

export interface DocumentGrantView {
  id: string;
  organizationId: string;
  targetType: DocumentTargetType;
  targetRole: string | null;
  targetSeatId: string | null;
  /** Resolved for display (null if the seat is gone). */
  targetSeatName: string | null;
  targetDepartmentId: string | null;
  targetDepartmentName: string | null;
  targetMembershipId: string | null;
  /** Resolved display name (null if the member is gone). */
  targetMemberName: string | null;
  aiSuggested: boolean;
}

export interface DocumentView {
  id: string;
  title: string;
  description: string | null;
  category: string;
  fileObjectId: string | null;
  /** From the FileObject (null if unattached). */
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  uploaderNameSnapshot: string;
  /** The orgs this doc is linked to. */
  orgIds: string[];
  /** Viewer holds manage_documents (or is a superadmin). */
  canManage: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentDetail extends DocumentView {
  /** ONLY populated when `canManage`; otherwise `[]`. */
  grants: DocumentGrantView[];
}

export interface GrantInput {
  organizationId: string;
  targetType: DocumentTargetType;
  targetRole?: string | null;
  targetSeatId?: string | null;
  targetDepartmentId?: string | null;
  targetMembershipId?: string | null;
  /** Marks a grant that originated from an accepted AI suggestion. */
  aiSuggested?: boolean;
}

export interface AccessSuggestion {
  targetType: 'role' | 'seat';
  targetRole: string | null;
  targetSeatId: string | null;
  /** e.g. "Warehouse Lead (seat)" / "Department lead (role)". */
  targetLabel: string;
  reason: string;
}

export interface DocumentDownloadUrl {
  url: string;
  filename: string;
  contentType: string;
}

// ── Request shapes ────────────────────────────────────────────────────────

export interface ListDocumentsQuery {
  category?: string;
  search?: string;
}

export interface SuggestAccessRequest {
  fileObjectId: string;
  title: string;
  description?: string;
  category: string;
}

export interface CreateDocumentRequest {
  fileObjectId: string;
  title: string;
  description?: string;
  category: string;
  orgIds: string[];
  grants: GrantInput[];
}

export interface UpdateDocumentRequest {
  title?: string;
  description?: string;
  category?: string;
  orgIds?: string[];
  grants?: GrantInput[];
}

// ── AI-disabled typed error (mirrors rockAssistant.api.ts) ────────────────

/** Thrown when the server reports AI is not configured (503 `ai_disabled`). */
export class AiDisabledError extends Error {
  constructor() {
    super('AI assistance is not configured for this organization.');
    this.name = 'AiDisabledError';
  }
}

/** True when an error is the 503 `ai_disabled` envelope. */
function isAiDisabled(err: unknown): boolean {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { error?: unknown } } }).response;
    return response?.status === 503 && response.data?.error === 'ai_disabled';
  }
  return false;
}

// ── Endpoints ─────────────────────────────────────────────────────────────

export async function listDocuments(q: ListDocumentsQuery = {}): Promise<DocumentView[]> {
  const params = new URLSearchParams();
  if (q.category) params.set('category', q.category);
  if (q.search) params.set('search', q.search);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: DocumentView[] }>(`/api/documents${qs}`);
  return res.data.data;
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  const res = await apiClient.get<{ data: DocumentDetail }>(`/api/documents/${encodeURIComponent(id)}`);
  return res.data.data;
}

export async function getDocumentDownloadUrl(id: string): Promise<DocumentDownloadUrl> {
  const res = await apiClient.get<{ data: DocumentDownloadUrl }>(
    `/api/documents/${encodeURIComponent(id)}/download-url`,
  );
  return res.data.data;
}

/**
 * Ask the assistant which roles/seats need access to a just-uploaded document.
 * Resolves with the suggestions; throws {@link AiDisabledError} when AI is
 * switched off (503), and otherwise re-throws the axios error (carrying
 * `ai_could_not_suggest` / `ai_error` / `file_not_found`) for the caller to map.
 */
export async function suggestDocumentAccess(body: SuggestAccessRequest): Promise<AccessSuggestion[]> {
  try {
    const res = await apiClient.post<{ data: { suggestions: AccessSuggestion[] } }>(
      '/api/documents/suggest-access',
      body,
    );
    return res.data.data.suggestions;
  } catch (err) {
    if (isAiDisabled(err)) throw new AiDisabledError();
    throw err;
  }
}

export async function createDocument(body: CreateDocumentRequest): Promise<DocumentDetail> {
  const res = await apiClient.post<{ data: DocumentDetail }>('/api/documents', body);
  return res.data.data;
}

export async function updateDocument(id: string, body: UpdateDocumentRequest): Promise<DocumentDetail> {
  const res = await apiClient.put<{ data: DocumentDetail }>(`/api/documents/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

export async function deleteDocument(id: string): Promise<void> {
  await apiClient.delete(`/api/documents/${encodeURIComponent(id)}`);
}
